import React, { useState, useRef } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAutosave } from "@/lib/useAutosave";
import { useDraftBackup, DraftRecoverBanner } from "@/components/app/DraftRecover";
import { LayoutList, X, Save, Loader2, Plus, Trash2 } from "lucide-react";
import FirmasEditor from "@/components/app/FirmasEditor";
import AttachFiles from "@/components/app/AttachFiles";
import RichText from "@/components/app/RichText";

const PERIODOS = ["Día", "Semanal", "Quincenal", "Mensual", "Ciclo completo"];

const Field = ({ label, value, onChange, testid, placeholder, area }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.15em] text-nardo-500 font-semibold">{label}</label>
    {area ? (
      <div className="mt-1"><RichText value={value} onChange={onChange} placeholder={placeholder} testid={testid} minRows={2} /></div>
    ) : (
      <input value={value} onChange={onChange} data-testid={testid} placeholder={placeholder}
        className="mt-1 w-full border border-nardo-200 rounded-lg px-2.5 py-1.5 text-sm text-navy outline-none focus:ring-2 focus:ring-navy/30" />
    )}
  </div>
);

export default function PlaneacionAreas({ initial, onSaved, onCancel }) {
  const [titulo, setTitulo] = useState(initial?.proyecto || "");
  const [saving, setSaving] = useState(false);
  const [savedId, setSavedId] = useState(initial?.id || null);
  const [d, setD] = useState(() => ({
    title: initial?.data?.title || "PLANO DIDÁCTICO ÁREAS ESPECIALES",
    subtitle: initial?.data?.subtitle || "",
    membrete_left: initial?.data?.membrete_left || "",
    membrete_right: initial?.data?.membrete_right || "",
    periodicidad: initial?.data?.periodicidad || "Semanal",
    trimestre: initial?.data?.trimestre || "",
    mes: initial?.data?.mes || "",
    area: initial?.data?.area || "",
    grado: initial?.data?.grado || "",
    grupo: initial?.data?.grupo || "",
    tema: initial?.data?.tema || "",
    proyecto_txt: initial?.data?.proyecto_txt || "",
    proposito: initial?.data?.proposito || "",
    campo_formativo: initial?.data?.campo_formativo || "",
    ejes: initial?.data?.ejes || "",
    sesiones: Array.isArray(initial?.data?.sesiones) && initial.data.sesiones.length ? initial.data.sesiones : [{ sesion: "Sesión 1", actividad: "" }],
    materiales: Array.isArray(initial?.data?.materiales) && initial.data.materiales.length ? initial.data.materiales : [{ material: "", evidencia: "" }],
    instrumento: {
      saber_que: initial?.data?.instrumento?.saber_que || "LISTA DE COTEJO",
      saber_hacer: initial?.data?.instrumento?.saber_hacer || "CUESTIONARIO",
      saber_ser: initial?.data?.instrumento?.saber_ser || "GUÍA DE OBSERVACIÓN",
    },
    firma_docente_name: initial?.data?.firma_docente_name || "",
    firma_docente_rol: initial?.data?.firma_docente_rol || "Docente",
    firma_director_name: initial?.data?.firma_director_name || "",
    firma_director_rol: initial?.data?.firma_director_rol || "Director(a)",
    firma_coord_name: initial?.data?.firma_coord_name || "",
    firma_coord_rol: initial?.data?.firma_coord_rol || "Coordinador(a)",
    _files: initial?.data?._files || {},
  }));

  const set = (k) => (e) => setD((s) => ({ ...s, [k]: e.target.value }));
  const setFiles = (arr) => setD((s) => ({ ...s, _files: { ...(s._files || {}), general: arr } }));
  const setIe = (k) => (e) => setD((s) => ({ ...s, instrumento: { ...s.instrumento, [k]: e.target.value } }));
  const setSes = (i, k) => (e) => setD((s) => ({ ...s, sesiones: s.sesiones.map((r, idx) => (idx === i ? { ...r, [k]: e.target.value } : r)) }));
  const addSes = () => setD((s) => ({ ...s, sesiones: [...s.sesiones, { sesion: `Sesión ${s.sesiones.length + 1}`, actividad: "" }] }));
  const delSes = (i) => setD((s) => ({ ...s, sesiones: s.sesiones.filter((_, idx) => idx !== i) }));
  const setMat = (i, k) => (e) => setD((s) => ({ ...s, materiales: s.materiales.map((r, idx) => (idx === i ? { ...r, [k]: e.target.value } : r)) }));
  const addMat = () => setD((s) => ({ ...s, materiales: [...s.materiales, { material: "", evidencia: "" }] }));
  const delMat = (i) => setD((s) => ({ ...s, materiales: s.materiales.filter((_, idx) => idx !== i) }));

  const savedSnap = useRef("");
  const savingRef = useRef(false);
  const draft = useDraftBackup({ tipo: "areas", initialId: initial?.id });
  const applyDraft = (p) => { if (!p) return; setTitulo(p.titulo || ""); if (p.d) setD(p.d); };
  const save = async (silent = false) => {
    const body = { proyecto: titulo.trim() || "Plano sin título", tipo: "areas", color: "#f4c430", formato: 6, data: d };
    const snap = JSON.stringify(body);
    if (silent && snap === savedSnap.current) return;
    if (savingRef.current) return;
    savingRef.current = true;
    if (!silent) setSaving(true);
    try {
      if (savedId) await api.put(`/docente/planeaciones/${savedId}`, body);
      else { const { data: res } = await api.post("/docente/planeaciones", body); setSavedId(res.id); }
      savedSnap.current = snap;
      draft.clear();
      if (!silent) onSaved();
    } catch (e) { if (!silent) toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
    finally { savingRef.current = false; if (!silent) setSaving(false); }
  };
  const baseWatch = useRef();
  const watch = JSON.stringify(d) + "|" + titulo;
  if (baseWatch.current === undefined) baseWatch.current = watch;
  useAutosave(watch, () => { draft.backup({ titulo, d }); save(true); }, { enabled: watch !== baseWatch.current });

  return (
    <div className="fixed inset-0 z-[80] bg-slate-900/80 flex items-start justify-center overflow-y-auto p-4" onClick={onCancel} data-testid="pa-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full my-8 max-w-4xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 rounded-t-2xl text-white" style={{ background: "#b8860b" }}>
          <div className="font-display font-bold flex items-center gap-2"><LayoutList className="w-4 h-4" /> {initial?.id ? "Editar" : "Nuevo"} plano didáctico · Áreas especiales</div>
          <button onClick={onCancel} className="text-white/80 hover:text-white p-1"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4 sm:p-5 space-y-4">
          <DraftRecoverBanner pending={draft.pending} onRecover={() => { applyDraft(draft.pending); draft.consumed(); }} onDismiss={draft.dismiss} />
          <input value={titulo} onChange={(e) => setTitulo(e.target.value)} placeholder="Nombre del plano (para tu lista, ej. Música 2°A Septiembre)" data-testid="pa-titulo"
            className="w-full border border-nardo-200 rounded-lg px-3 py-2.5 text-sm text-navy outline-none focus:border-navy font-semibold" />

          <div className="grid sm:grid-cols-2 gap-3 p-3 rounded-lg border border-dashed border-nardo-300">
            <div className="sm:col-span-2 text-[10px] uppercase tracking-[0.2em] text-nardo-500 font-semibold">Encabezado / membrete (editable · manual)</div>
            <Field label="Título" value={d.title} onChange={set("title")} testid="pa-title" placeholder="PLANO DIDÁCTICO ÁREAS ESPECIALES" />
            <Field label="Subtítulo (opcional)" value={d.subtitle} onChange={set("subtitle")} testid="pa-subtitle" />
            <Field label="Membrete izquierdo (ej. PREESCOLAR / CCT / AN1633)" value={d.membrete_left} onChange={set("membrete_left")} testid="pa-membrete_left" area />
            <Field label="Membrete derecho (ej. PRIMARIA / CCT / PPR785-14)" value={d.membrete_right} onChange={set("membrete_right")} testid="pa-membrete_right" area />
            <p className="sm:col-span-2 text-[10px] text-nardo-400">Se genera sin logo, sin marca de agua y sin nombre de colegio. Escribe tú el membrete si lo necesitas (o déjalo vacío).</p>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-nardo-500 font-semibold mb-1.5">Periodicidad (se guarda así)</div>
            <div className="flex flex-wrap gap-2" data-testid="pa-periodos">
              {PERIODOS.map((per) => (
                <button key={per} type="button" onClick={() => setD((s) => ({ ...s, periodicidad: per }))} data-testid={`pa-per-${per}`}
                  className={`text-sm font-semibold px-3.5 py-2 rounded-lg border transition-colors ${d.periodicidad === per ? "text-white border-transparent" : "border-nardo-200 text-nardo-600 hover:bg-nardo-50"}`}
                  style={d.periodicidad === per ? { background: "#b8860b" } : {}}>{per}</button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 p-3 rounded-lg" style={{ background: "#fbe7b2" }}>
            <Field label="Trimestre" value={d.trimestre} onChange={set("trimestre")} testid="pa-trimestre" />
            <Field label="Mes" value={d.mes} onChange={set("mes")} testid="pa-mes" />
            <Field label="Área" value={d.area} onChange={set("area")} testid="pa-area" />
            <Field label="Grado" value={d.grado} onChange={set("grado")} testid="pa-grado" />
            <Field label="Grupo" value={d.grupo} onChange={set("grupo")} testid="pa-grupo" />
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Tema" value={d.tema} onChange={set("tema")} testid="pa-tema" area />
            <Field label="Proyecto" value={d.proyecto_txt} onChange={set("proyecto_txt")} testid="pa-proyecto_txt" area />
            <Field label="Propósito general" value={d.proposito} onChange={set("proposito")} testid="pa-proposito" area />
            <Field label="Campo formativo" value={d.campo_formativo} onChange={set("campo_formativo")} testid="pa-campo_formativo" area />
            <Field label="Ejes articuladores" value={d.ejes} onChange={set("ejes")} testid="pa-ejes" area />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase tracking-[0.2em] text-nardo-500 font-semibold">Desarrollo de la actividad (sesiones)</div>
              <button type="button" onClick={addSes} data-testid="pa-add-ses" className="text-xs font-semibold inline-flex items-center gap-1 text-navy border border-navy/30 hover:bg-navy hover:text-white px-2.5 py-1.5 rounded-lg transition-colors"><Plus className="w-3.5 h-3.5" /> Agregar sesión</button>
            </div>
            <div className="space-y-2" data-testid="pa-sesiones">
              {d.sesiones.map((s, i) => (
                <div key={i} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-start border border-nardo-200 rounded-lg p-2" data-testid={`pa-ses-${i}`}>
                  <input value={s.sesion} onChange={setSes(i, "sesion")} placeholder={`Sesión ${i + 1}`} data-testid={`pa-ses-${i}-sesion`} className="sm:col-span-3 w-full border border-nardo-200 rounded-md px-2 py-1.5 text-sm text-navy outline-none focus:ring-2 focus:ring-navy/30" />
                  <div className="sm:col-span-8"><RichText value={s.actividad} onChange={setSes(i, "actividad")} placeholder="Actividad" minRows={2} testid={`pa-ses-${i}-actividad`} /></div>
                  <button type="button" onClick={() => delSes(i)} data-testid={`pa-del-ses-${i}`} className="sm:col-span-1 text-red-500 hover:text-red-700 flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase tracking-[0.2em] text-nardo-500 font-semibold">Material / recurso y evidencia</div>
              <button type="button" onClick={addMat} data-testid="pa-add-mat" className="text-xs font-semibold inline-flex items-center gap-1 text-navy border border-navy/30 hover:bg-navy hover:text-white px-2.5 py-1.5 rounded-lg transition-colors"><Plus className="w-3.5 h-3.5" /> Agregar fila</button>
            </div>
            <div className="space-y-2" data-testid="pa-materiales">
              {d.materiales.map((m, i) => (
                <div key={i} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-start border border-nardo-200 rounded-lg p-2" data-testid={`pa-mat-${i}`}>
                  <div className="sm:col-span-7"><RichText value={m.material} onChange={setMat(i, "material")} placeholder="Material / recurso (videos, láminas, tarea, frisos…)" minRows={2} testid={`pa-mat-${i}-material`} /></div>
                  <div className="sm:col-span-4"><RichText value={m.evidencia} onChange={setMat(i, "evidencia")} placeholder="Evidencia" minRows={2} testid={`pa-mat-${i}-evidencia`} /></div>
                  <button type="button" onClick={() => delMat(i)} data-testid={`pa-del-mat-${i}`} className="sm:col-span-1 text-red-500 hover:text-red-700 flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-3 p-3 rounded-lg border border-nardo-200">
            <div className="sm:col-span-3 text-[10px] uppercase tracking-[0.2em] text-nardo-500 font-semibold">Instrumento de evaluación</div>
            <Field label="Saber qué" value={d.instrumento.saber_que} onChange={setIe("saber_que")} testid="pa-saber_que" />
            <Field label="Saber hacer" value={d.instrumento.saber_hacer} onChange={setIe("saber_hacer")} testid="pa-saber_hacer" />
            <Field label="Saber ser" value={d.instrumento.saber_ser} onChange={setIe("saber_ser")} testid="pa-saber_ser" />
          </div>

          <AttachFiles value={d._files?.general || []} onChange={setFiles} base="pa-attach" color="#b8860b" />

          <FirmasEditor d={d} set={set} base="pa-firmas" color="#b8860b" />

          <div className="flex justify-end gap-2 pt-1">
            <span className="mr-auto self-center text-[11px] text-nardo-400" data-testid="pa-autosave-hint">Autoguardado activo</span>
            <button onClick={onCancel} className="px-4 py-2 rounded-lg border border-nardo-200 text-sm text-nardo-600 hover:bg-nardo-50" data-testid="pa-cancel">Cerrar</button>
            <button onClick={() => save(false)} disabled={saving} data-testid="pa-save" className="px-4 py-2 rounded-lg text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50" style={{ background: "#b8860b" }}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar cambios y contenido
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
